import React, { useState, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { FaChartLine, FaClipboardList, FaSearch, FaBullhorn, FaChevronLeft, FaChevronRight, FaTimes, FaCheck, FaFileAlt, FaShieldAlt, FaArrowUp, FaBolt, FaGlobe, FaListUl, FaUsers, FaBrain, FaFire, FaComment, FaHashtag, FaHeart, FaSpinner } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';

const REPORT_TYPES = [
    { key: 'executive', name: 'Executive Summary', desc: 'Quick overview for leadership meetings', pages: '1-2', Icon: FaChartLine },
    { key: 'operational', name: 'Operational Report', desc: 'Detailed analysis for your team', pages: '3-6', Icon: FaClipboardList },
    { key: 'deep_analytics', name: 'Deep Analytics', desc: 'Comprehensive data deep-dive', pages: '6-15', Icon: FaSearch },
    { key: 'campaign', name: 'Campaign Report', desc: 'Campaign performance analysis', pages: '2-5', Icon: FaBullhorn },
];

const ICON_MAP = {
    chart: FaChartLine, heart: FaHeart, shield: FaShieldAlt, 'arrow-up': FaArrowUp,
    bolt: FaBolt, globe: FaGlobe, list: FaListUl, users: FaUsers, brain: FaBrain,
    fire: FaFire, 'chart-line': FaChartLine, comment: FaComment, hashtag: FaHashtag,
    bullhorn: FaBullhorn,
};

const ALL_SECTIONS = [
    { key: 'kpi_summary', title: 'KPI Summary', desc: 'Health score & key metrics', icon: 'chart', group: ['executive','operational','deep_analytics'] },
    { key: 'sentiment', title: 'Sentiment Analysis', desc: 'Sentiment gauges & timeline', icon: 'heart', group: ['executive','operational','deep_analytics'] },
    { key: 'threats', title: 'Top Threats', desc: 'Negative trending narratives', icon: 'shield', group: ['executive','operational','deep_analytics'] },
    { key: 'opportunities', title: 'Opportunities', desc: 'Positive trending narratives', icon: 'arrow-up', group: ['executive','operational','deep_analytics'] },
    { key: 'action_queue', title: 'Action Queue', desc: 'Recommended actions summary', icon: 'bolt', group: ['executive','operational','deep_analytics'] },
    { key: 'platform_distribution', title: 'Platform Distribution', desc: 'Posts by platform breakdown', icon: 'globe', group: ['executive','operational','deep_analytics'] },
    { key: 'narratives', title: 'Full Narratives', desc: 'Complete narrative list', icon: 'list', group: ['operational','deep_analytics'] },
    { key: 'sources', title: 'Top Sources', desc: 'Most active accounts', icon: 'users', group: ['operational','deep_analytics'] },
    { key: 'entities', title: 'Entity Analysis', desc: 'People, orgs, locations', icon: 'brain', group: ['operational','deep_analytics'] },
    { key: 'emotions', title: 'Emotion Breakdown', desc: 'Emotional distribution', icon: 'fire', group: ['operational','deep_analytics'] },
    { key: 'activity_charts', title: 'Activity Charts', desc: 'Hourly & daily activity', icon: 'chart-line', group: ['deep_analytics'] },
    { key: 'posts', title: 'Recent Posts', desc: 'Most positive & negative', icon: 'comment', group: ['deep_analytics'] },
    { key: 'hashtags', title: 'Trending Hashtags', desc: 'Top hashtags analysis', icon: 'hashtag', group: ['deep_analytics'] },
    { key: 'campaigns', title: 'Campaign Performance', desc: 'Campaign metrics & growth', icon: 'bullhorn', group: ['campaign'] },
];

const ACCENT_COLORS = [
    { name: 'Blue', value: '#0a84ff' },
    { name: 'Purple', value: '#a78bfa' },
    { name: 'Green', value: '#22c55e' },
    { name: 'Red', value: '#ef4444' },
    { name: 'Orange', value: '#f97316' },
    { name: 'Teal', value: '#14b8a6' },
];

const ReportBuilderModal = ({ visible, onClose, orgId, theme }) => {
    const { t } = useTranslation();
    const [step, setStep] = useState(1);
    const [selectedType, setSelectedType] = useState(null);
    const [enabledSections, setEnabledSections] = useState({});
    const [dateRange, setDateRange] = useState('7d');
    const [customTitle, setCustomTitle] = useState('');
    const [accentColor, setAccentColor] = useState('#0a84ff');
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState(null);

    const T = theme || {};
    const isRTL = i18n.language === 'he';

    const availableSections = useMemo(() => {
        if (!selectedType) return [];
        return ALL_SECTIONS.filter(s => s.group.includes(selectedType));
    }, [selectedType]);

    const handleTypeSelect = (key) => {
        setSelectedType(key);
        const sections = ALL_SECTIONS.filter(s => s.group.includes(key));
        const defaults = {};
        sections.forEach(s => { defaults[s.key] = true; });
        setEnabledSections(defaults);
    };

    const toggleSection = (key) => {
        setEnabledSections(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const handleGenerate = async () => {
        setGenerating(true);
        setError(null);
        try {
            const config = {
                report_type: selectedType,
                org_id: orgId,
                title: customTitle || undefined,
                date_range: dateRange,
                sections: Object.entries(enabledSections).map(([key, enabled]) => ({ key, enabled })),
                branding: { accent_color: accentColor },
            };
            const ListsManager = (await import('../services/ListsManager')).default;
            const blob = await ListsManager.generateReport(config);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${(customTitle || 'Brand_Intelligence_Report').replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            onClose();
        } catch (err) {
            setError(err.message || t('app.reportBuilder.failedGenerate'));
        } finally {
            setGenerating(false);
        }
    };

    const handleClose = () => {
        if (!generating) {
            setStep(1);
            setSelectedType(null);
            setEnabledSections({});
            setError(null);
            onClose();
        }
    };

    if (!visible) return null;

    const canNext = step === 1 ? !!selectedType : true;

    const modal = (
        <div style={{ position: 'fixed', inset: 0, zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div onClick={handleClose} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }} />
            <div style={{ position: 'relative', width: '100%', maxWidth: 680, maxHeight: '90vh', margin: '0 20px', borderRadius: 20, background: T.modalBg || '#1E1E23', border: `1px solid ${T.cardBorder || 'rgba(255,255,255,0.06)'}`, display: 'flex', flexDirection: 'column', overflow: 'hidden', direction: isRTL ? 'rtl' : 'ltr' }}>
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 16px', borderBottom: `1px solid ${T.cardBorder || 'rgba(255,255,255,0.06)'}` }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <FaFileAlt size={16} color={T.primary || '#0a84ff'} />
                        <span style={{ color: T.textMain || '#f0f0f5', fontSize: 16, fontWeight: 700 }}>{t('app.reportBuilder.generateReport')}</span>
                    </div>
                    <button onClick={handleClose} style={{ background: 'none', border: 'none', color: T.textTertiary || '#666', cursor: 'pointer', padding: 4 }}>
                        <FaTimes size={16} />
                    </button>
                </div>

                {/* Step indicator */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '12px 0' }}>
                    {[1, 2, 3].map(s => (
                        <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ width: 28, height: 28, borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, background: step >= s ? (T.primary || '#0a84ff') : (T.surface || 'rgba(255,255,255,0.05)'), color: step >= s ? '#fff' : (T.textTertiary || '#666'), transition: 'all 0.2s' }}>
                                {step > s ? <FaCheck size={10} /> : s}
                            </div>
                            {s < 3 && <div style={{ width: 32, height: 2, borderRadius: 1, background: step > s ? (T.primary || '#0a84ff') : (T.surface || 'rgba(255,255,255,0.05)'), transition: 'all 0.2s' }} />}
                        </div>
                    ))}
                </div>

                {/* Content */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '8px 24px 24px' }}>
                    {step === 1 && (
                        <div>
                            <div style={{ color: T.textSecondary || '#aaa', fontSize: 13, marginBottom: 16 }}>{t('app.reportBuilder.chooseType')}</div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                {REPORT_TYPES.map(rt => {
                                    const isSelected = selectedType === rt.key;
                                    return (
                                        <div key={rt.key} onClick={() => handleTypeSelect(rt.key)} style={{
                                            padding: 20, borderRadius: 14, cursor: 'pointer', transition: 'all 0.2s',
                                            background: isSelected ? `${T.primary || '#0a84ff'}15` : (T.surface || 'rgba(255,255,255,0.03)'),
                                            border: `1px solid ${isSelected ? (T.primary || '#0a84ff') : (T.cardBorder || 'rgba(255,255,255,0.06)')}`,
                                            boxShadow: isSelected ? `0 0 20px ${T.primary || '#0a84ff'}20` : 'none',
                                        }}>
                                            <rt.Icon size={22} color={isSelected ? (T.primary || '#0a84ff') : (T.textTertiary || '#666')} />
                                            <div style={{ color: T.textMain || '#f0f0f5', fontSize: 14, fontWeight: 600, marginTop: 10 }}>{t(`app.reportBuilder.types.${rt.key}.name`)}</div>
                                            <div style={{ color: T.textSecondary || '#aaa', fontSize: 11, marginTop: 4, lineHeight: 1.4 }}>{t(`app.reportBuilder.types.${rt.key}.desc`)}</div>
                                            <div style={{ color: T.textTertiary || '#666', fontSize: 10, marginTop: 8, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{t('app.reportBuilder.pages', { pages: rt.pages })}</div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div>
                            <div style={{ color: T.textSecondary || '#aaa', fontSize: 13, marginBottom: 16 }}>{t('app.reportBuilder.customizeSections')}</div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                                {availableSections.map(sec => {
                                    const enabled = enabledSections[sec.key] !== false;
                                    const SIcon = ICON_MAP[sec.icon] || FaFileAlt;
                                    return (
                                        <div key={sec.key} onClick={() => toggleSection(sec.key)} style={{
                                            display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', borderRadius: 12, cursor: 'pointer',
                                            background: enabled ? `${T.primary || '#0a84ff'}10` : (T.surface || 'rgba(255,255,255,0.03)'),
                                            border: `1px solid ${enabled ? `${T.primary || '#0a84ff'}40` : (T.cardBorder || 'rgba(255,255,255,0.06)')}`,
                                            transition: 'all 0.15s',
                                        }}>
                                            <SIcon size={14} color={enabled ? (T.primary || '#0a84ff') : (T.textTertiary || '#666')} />
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ color: T.textMain || '#f0f0f5', fontSize: 12, fontWeight: 600 }}>{t(`app.reportBuilder.sections.${sec.key}.title`)}</div>
                                                <div style={{ color: T.textTertiary || '#666', fontSize: 10, marginTop: 2 }}>{t(`app.reportBuilder.sections.${sec.key}.desc`)}</div>
                                            </div>
                                            <div style={{
                                                width: 36, height: 20, borderRadius: 10, padding: 2,
                                                background: enabled ? (T.primary || '#0a84ff') : 'rgba(255,255,255,0.1)',
                                                transition: 'background 0.2s', flexShrink: 0,
                                            }}>
                                                <div style={{
                                                    width: 16, height: 16, borderRadius: 8, background: '#fff',
                                                    transform: enabled ? `translateX(${isRTL ? '-16px' : '16px'})` : 'translateX(0)',
                                                    transition: 'transform 0.2s',
                                                }} />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <div>
                            <div style={{ color: T.textSecondary || '#aaa', fontSize: 13, marginBottom: 16 }}>{t('app.reportBuilder.reportSettings')}</div>

                            {/* Date Range */}
                            <div style={{ marginBottom: 20 }}>
                                <div style={{ color: T.textMain || '#f0f0f5', fontSize: 12, fontWeight: 600, marginBottom: 8 }}>{t('app.reportBuilder.dateRange')}</div>
                                <div style={{ display: 'flex', gap: 0, borderRadius: 10, overflow: 'hidden', border: `1px solid ${T.cardBorder || 'rgba(255,255,255,0.06)'}` }}>
                                    {['24h', '7d', '30d'].map(r => (
                                        <button key={r} onClick={() => setDateRange(r)} style={{
                                            flex: 1, padding: '10px 0', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                                            background: dateRange === r ? (T.primary || '#0a84ff') : (T.surface || 'rgba(255,255,255,0.03)'),
                                            color: dateRange === r ? '#fff' : (T.textSecondary || '#aaa'),
                                            transition: 'all 0.15s',
                                        }}>{r === '24h' ? t('app.reportBuilder.hours24') : r === '7d' ? t('app.reportBuilder.days7') : t('app.reportBuilder.days30')}</button>
                                    ))}
                                </div>
                            </div>

                            {/* Custom Title */}
                            <div style={{ marginBottom: 20 }}>
                                <div style={{ color: T.textMain || '#f0f0f5', fontSize: 12, fontWeight: 600, marginBottom: 8 }}>{t('app.reportBuilder.reportTitle')}</div>
                                <input
                                    value={customTitle}
                                    onChange={e => setCustomTitle(e.target.value)}
                                    placeholder={t('app.reportBuilder.titlePlaceholder')}
                                    style={{
                                        width: '100%', padding: '10px 14px', borderRadius: 10, fontSize: 13,
                                        background: T.surface || 'rgba(255,255,255,0.03)',
                                        border: `1px solid ${T.cardBorder || 'rgba(255,255,255,0.06)'}`,
                                        color: T.textMain || '#f0f0f5', outline: 'none', boxSizing: 'border-box',
                                    }}
                                />
                            </div>

                            {/* Accent Color */}
                            <div style={{ marginBottom: 20 }}>
                                <div style={{ color: T.textMain || '#f0f0f5', fontSize: 12, fontWeight: 600, marginBottom: 8 }}>{t('app.reportBuilder.accentColor')}</div>
                                <div style={{ display: 'flex', gap: 10 }}>
                                    {ACCENT_COLORS.map(c => (
                                        <div key={c.value} onClick={() => setAccentColor(c.value)} style={{
                                            width: 36, height: 36, borderRadius: 18, background: c.value, cursor: 'pointer',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            border: accentColor === c.value ? '3px solid #fff' : '2px solid transparent',
                                            boxShadow: accentColor === c.value ? `0 0 12px ${c.value}60` : 'none',
                                            transition: 'all 0.15s',
                                        }}>
                                            {accentColor === c.value && <FaCheck size={12} color="#fff" />}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Preview */}
                            <div style={{ padding: 16, borderRadius: 12, background: T.surface || 'rgba(255,255,255,0.03)', border: `1px solid ${T.cardBorder || 'rgba(255,255,255,0.06)'}` }}>
                                <div style={{ fontSize: 10, fontWeight: 700, color: T.textTertiary || '#666', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>{t('app.reportBuilder.preview')}</div>
                                <div style={{ [`border${isRTL ? 'Right' : 'Left'}`]: `4px solid ${accentColor}`, [`padding${isRTL ? 'Right' : 'Left'}`]: 12 }}>
                                    <div style={{ color: T.textMain || '#f0f0f5', fontSize: 15, fontWeight: 700 }}>{customTitle || t('app.reportBuilder.titlePlaceholder')}</div>
                                    <div style={{ color: T.textSecondary || '#aaa', fontSize: 11, marginTop: 4 }}>
                                        {selectedType && t(`app.reportBuilder.types.${selectedType}.name`)} &middot; {dateRange === '24h' ? t('app.reportBuilder.last24Hours') : dateRange === '7d' ? t('app.reportBuilder.last7Days') : t('app.reportBuilder.last30Days')}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {error && (
                        <div style={{ marginTop: 12, padding: '10px 14px', borderRadius: 8, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#f87171', fontSize: 12 }}>
                            {error}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', borderTop: `1px solid ${T.cardBorder || 'rgba(255,255,255,0.06)'}` }}>
                    <div>
                        {step > 1 && (
                            <button onClick={() => setStep(s => s - 1)} disabled={generating} style={{
                                display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8,
                                background: 'none', border: `1px solid ${T.cardBorder || 'rgba(255,255,255,0.06)'}`,
                                color: T.textSecondary || '#aaa', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                            }}>
                                {isRTL ? <FaChevronRight size={10} /> : <FaChevronLeft size={10} />} {t('app.reportBuilder.back')}
                            </button>
                        )}
                    </div>
                    <div>
                        {step < 3 ? (
                            <button onClick={() => setStep(s => s + 1)} disabled={!canNext} style={{
                                padding: '8px 24px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 600, cursor: canNext ? 'pointer' : 'not-allowed',
                                background: canNext ? (T.primary || '#0a84ff') : 'rgba(255,255,255,0.05)',
                                color: canNext ? '#fff' : (T.textTertiary || '#666'),
                                transition: 'all 0.15s',
                            }}>{t('app.reportBuilder.next')}</button>
                        ) : (
                            <button onClick={handleGenerate} disabled={generating} style={{
                                display: 'flex', alignItems: 'center', gap: 8, padding: '10px 24px', borderRadius: 10, border: 'none',
                                background: generating ? 'rgba(255,255,255,0.05)' : `linear-gradient(135deg, ${T.primary || '#0a84ff'}, ${T.purple || '#a78bfa'})`,
                                color: '#fff', fontSize: 13, fontWeight: 700, cursor: generating ? 'wait' : 'pointer',
                                transition: 'all 0.2s',
                            }}>
                                {generating ? (
                                    <><FaSpinner size={12} style={{ animation: 'spin 1s linear infinite' }} /> {t('app.reportBuilder.generating')}</>
                                ) : (
                                    <><FaFileAlt size={12} /> {t('app.reportBuilder.generateDownload')}</>
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </div>
            <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        </div>
    );

    return ReactDOM.createPortal(modal, document.body);
};

export default ReportBuilderModal;
